import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, ShoppingCart, Heart, Download, KeyRound, Hand, ShieldCheck, ChevronRight, MessageSquareReply, Store, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaSrc, money, fmtDate, DELIVERY_LABEL, num } from "@/lib/api";
import { Stars, EmptyState, ProductGrid, RichText } from "@/components/vaulted";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const DIcon = { download: Download, licence_key: KeyRound, manual: Hand };

function Gallery({ images, title }) {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-video overflow-hidden rounded-xl border border-[#27272A] bg-[#111116]">
        {images.length ? <img data-testid="product-main-image" src={mediaSrc(images[i]?.url)} alt={title} className="h-full w-full object-cover" /> :
          <div className="h-full w-full grid-lines" />}
      </div>
      {images.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-thin">
          {images.map((im, idx) => (
            <button key={im.id} onClick={() => setI(idx)} data-testid={`product-thumb-${idx}`}
              className={cn("aspect-video w-24 shrink-0 overflow-hidden rounded-md border transition-[border-color]", idx === i ? "border-violet-500" : "border-[#27272A] hover:border-zinc-600")}>
              <img src={mediaSrc(im.url)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Reviews({ p }) {
  return (
    <section className="mt-10" data-testid="product-reviews">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-base font-semibold text-white">Reviews</h2>
        {p.rating_count > 0 && <span className="flex items-center gap-2 text-sm text-zinc-400"><Stars value={p.rating_avg} />{p.rating_avg.toFixed(1)} · {p.rating_count}</span>}
      </div>
      {p.reviews.length === 0 ? (
        <EmptyState title="No reviews yet" body="Only customers with a completed purchase can leave a review." testId="product-reviews-empty" />
      ) : (
        <div className="space-y-3">
          {p.reviews.map((r) => (
            <div key={r.id} className="v-card p-4" data-testid={`review-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-white">{r.username}</span>
                {r.verified_purchase && <span className="flex items-center gap-1 rounded border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400"><ShieldCheck className="h-3 w-3" />Verified Purchase</span>}
                <Stars value={r.rating} className="ml-auto" />
              </div>
              <p className="mt-2 text-sm text-zinc-300 whitespace-pre-line">{r.comment}</p>
              <p className="mt-2 text-[11px] text-zinc-600">{fmtDate(r.created_at)}</p>
              {r.seller_reply && (
                <div className="mt-3 rounded-md border-l-2 border-violet-500 bg-[#111116] p-3">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-violet-300"><MessageSquareReply className="h-3.5 w-3.5" />Seller response</p>
                  <p className="mt-1 text-sm text-zinc-300">{r.seller_reply.text}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FortnitePanel({ fn }) {
  const stats = [["Skins", num(fn.skins_count)], ["V-Bucks", num(fn.vbucks)], ["Account level", num(fn.level)], ["Platforms", fn.platforms?.join(", ") || "—"]];
  const flags = [["Email changeable", fn.email_changeable], ["Full access", fn.full_access]];
  return (
    <section className="mt-6 v-panel p-5" data-testid="fortnite-specs">
      <p className="v-eyebrow">Account details</p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map(([k, v]) => <div key={k} className="rounded-md border border-[#27272A] bg-[#18181B] p-3"><p className="text-[11px] text-zinc-500">{k}</p><p className="mt-0.5 text-sm font-semibold text-white" data-testid={`fn-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</p></div>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {flags.map(([k, v]) => <span key={k} className={cn("flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs", v ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-[#27272A] text-zinc-500")}>{v ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{k}</span>)}
      </div>
      {fn.rare_skins?.length > 0 && (
        <div className="mt-4"><p className="mb-2 text-xs font-medium text-zinc-300">Rare skins</p>
          <div className="flex flex-wrap gap-1.5" data-testid="fn-rare-skins">{fn.rare_skins.map((r) => <span key={r} className="rounded border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-xs text-violet-200">{r}</span>)}</div></div>
      )}
    </section>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [qty, setQty] = useState(1);
  const [wish, setWish] = useState(false);
  const [more, setMore] = useState([]);
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setP(null); setErr(null);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data); setQty(r.data.min_quantity || 1);
      api.get("/products", { params: { seller: r.data.seller.id, limit: 5 } }).then((m) => setMore(m.data.items.filter((x) => x.id !== r.data.id).slice(0, 4)));
    }).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => { if (user && p) api.get("/account/wishlist/ids").then((r) => setWish(r.data.includes(p.id))).catch(() => {}); }, [user, p]);

  if (err) return <div className="container-v py-20"><EmptyState title="Product not found" body={err} action={<Link to="/marketplace" className="v-btn-ghost v-btn-sm">Back to marketplace</Link>} /></div>;
  if (!p) return <div className="container-v py-10"><div className="aspect-video max-w-3xl animate-pulse rounded-xl bg-zinc-900" /></div>;

  const Icon = DIcon[p.delivery_method];
  const maxQty = p.delivery_method === "download" ? 1 : Math.min(20, p.stock ?? 20);
  const own = user && user.seller_id === p.seller.id;
  const toggleWish = async () => {
    if (!user) return nav(`/login?next=/product/${id}`);
    try {
      wish ? await api.delete(`/account/wishlist/${p.id}`) : await api.post(`/account/wishlist/${p.id}`);
      setWish(!wish); toast.success(wish ? "Removed from wishlist" : "Saved to wishlist");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="container-v py-8" data-testid="product-page">
      <nav className="mb-5 flex items-center gap-1.5 text-xs text-zinc-500">
        <Link to="/marketplace" className="hover:text-white">Marketplace</Link><ChevronRight className="h-3 w-3" />
        {p.category?.slug && <><Link to={`/marketplace?category=${p.category.slug}`} className="hover:text-white">{p.category.name}</Link><ChevronRight className="h-3 w-3" /></>}
        <span className="truncate text-zinc-400">{p.title}</span>
      </nav>
      <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        <div>
          <Gallery images={p.images} title={p.title} />
          {p.fortnite && <FortnitePanel fn={p.fortnite} />}
          <section className="mt-8">
            <h2 className="mb-3 text-base font-semibold text-white">Description</h2>
            {p.features?.length > 0 && <div className="mb-4 flex flex-wrap gap-1.5" data-testid="product-features">{p.features.map((f) => <span key={f} className="rounded-md border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-xs text-violet-200">{f}</span>)}</div>}
            <RichText text={p.description} testId="product-description" className="text-sm leading-relaxed text-zinc-300" />
            {p.tags?.length > 0 && <div className="mt-4 flex flex-wrap gap-1.5">{p.tags.map((t) => <span key={t} className="rounded border border-[#27272A] px-2 py-0.5 text-[11px] text-zinc-400">{t}</span>)}</div>}
          </section>
          <Reviews p={p} />
        </div>
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="v-panel p-5">
            <p className="text-xs text-zinc-500">{p.category?.name}</p>
            <h1 data-testid="product-title" className="mt-1 text-2xl font-semibold tracking-tight text-white">{p.title}</h1>
            <div className="mt-2 flex items-center gap-2 text-sm">
              {p.rating_count > 0 ? <><Stars value={p.rating_avg} /><span className="text-zinc-400">{p.rating_avg.toFixed(1)} ({p.rating_count})</span></> : <span className="text-zinc-600 text-xs">No reviews yet</span>}
            </div>
            <div className="mt-5 text-3xl font-semibold text-white" data-testid="product-price">{money(p.price)} <span className="text-xs font-normal text-zinc-500">AUD</span></div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-zinc-300"><Icon className="h-4 w-4 text-violet-400" />{DELIVERY_LABEL[p.delivery_method]}</div>
              <div data-testid="product-availability" className={cn("flex items-center gap-2", p.in_stock ? "text-emerald-400" : "text-red-400")}>
                <span className={cn("h-1.5 w-1.5 rounded-full", p.in_stock ? "bg-emerald-400" : "bg-red-400")} />
                {p.in_stock ? (p.stock == null ? "Available" : `${p.stock} in stock`) : "Out of stock"}
              </div>
            </div>
            {own ? (
              <Link to={`/seller/products/${p.id}`} className="mt-5 v-btn-ghost w-full" data-testid="product-edit-own">Edit your listing</Link>
            ) : (
              <div className="mt-5 flex gap-2">
                {maxQty > (p.min_quantity || 1) && (
                  <select data-testid="product-qty-select" value={qty} onChange={(e) => setQty(Number(e.target.value))} className="v-input w-20">
                    {Array.from({ length: maxQty - (p.min_quantity || 1) + 1 }).map((_, i) => <option key={i} value={i + (p.min_quantity || 1)}>{i + (p.min_quantity || 1)}</option>)}
                  </select>
                )}
                <button data-testid="add-to-cart-button" disabled={!p.in_stock} onClick={() => add(p, qty)} className="v-btn-primary flex-1"><ShoppingCart className="h-4 w-4" />Add to cart</button>
                <button data-testid="wishlist-toggle" onClick={toggleWish} className={cn("v-btn-ghost w-10 px-0", wish && "border-violet-500 text-violet-300")} aria-label="Wishlist"><Heart className={cn("h-4 w-4", wish && "fill-violet-400")} /></button>
              </div>
            )}
            <p className="mt-4 text-[11px] text-zinc-500">Delivered after a verified payment. See our <Link to="/legal/refunds" className="underline hover:text-white">Refund Policy</Link>.</p>
          </div>
          <Link to={`/store/${p.seller.slug}`} data-testid="product-seller-link" className="v-card flex items-center gap-3 p-4 hover:border-violet-500/40 transition-[border-color]">
            <div className="grid h-10 w-10 place-items-center rounded-md bg-violet-500/10 border border-violet-500/20"><Store className="h-4 w-4 text-violet-300" /></div>
            <div className="min-w-0">
              <p className="flex items-center gap-1 text-sm font-medium text-white">{p.seller.store_name}{p.seller.is_official && <BadgeCheck className="h-4 w-4 text-violet-400" />}</p>
              <p className="text-xs text-zinc-500">{p.seller.product_count} active listings</p>
            </div>
            <ChevronRight className="ml-auto h-4 w-4 text-zinc-600" />
          </Link>
        </aside>
      </div>
      {more.length > 0 && <section className="mt-14"><h2 className="mb-4 text-base font-semibold text-white">More from {p.seller.store_name}</h2><ProductGrid items={more} testId="more-from-seller" /></section>}
    </div>
  );
}
